/**
 * GenerationEventLog
 *
 * Hero status card + chronological agent timeline showing thinking and tool
 * activity during checklist generation. Renders during "generating" and "error"
 * states; returns null for "idle" and "complete".
 */

import {
    GenerationState,
    IEventLogEntry,
} from "@/api/clause-checklists/checklist-generation.types";
import { Markdown } from "@/components/shared/Markdown";
import { styles } from "@/config/project";
import { Box, keyframes, Paper, Stack, Typography } from "@mui/material";
import { CheckCircle2, Database, Loader2, Wrench, XCircle } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-6px); }
  to   { opacity: 1; transform: translateX(0); }
`;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOOL_DISPLAY_NAMES: Record<string, string> = {
    list_industries: "Searching industries",
    list_topics: "Browsing topics",
    list_all_clauses: "Scanning clause library",
    get_clauses_for_topics: "Fetching clauses",
    get_current_draft: "Loading current draft",
    submit_checklist_result: "Assembling checklist",
    submit_checklist_refinement: "Submitting changes",
};

function getToolDisplayName(toolName: string): string {
    if (TOOL_DISPLAY_NAMES[toolName]) return TOOL_DISPLAY_NAMES[toolName];
    // Fallback: capitalize and replace underscores
    return toolName.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Timeline item type + builder
// ---------------------------------------------------------------------------

type TimelineItem =
    | { type: "thinking"; entry: IEventLogEntry }
    | { type: "tool"; useEntry: IEventLogEntry; resultEntry?: IEventLogEntry }
    | { type: "progress"; entry: IEventLogEntry };

function buildTimelineItems(entries: IEventLogEntry[]): TimelineItem[] {
    const items: TimelineItem[] = [];
    const consumedIndices = new Set<number>();

    for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];

        if (entry.type === "thinking") {
            items.push({ type: "thinking", entry });
        } else if (entry.type === "tool_use") {
            // Find matching tool_result
            const resultEntry = entries.find(
                (e, j) =>
                    j > i &&
                    e.type === "tool_result" &&
                    e.toolName === entry.toolName &&
                    e.iteration === entry.iteration &&
                    !consumedIndices.has(j)
            );
            if (resultEntry) {
                consumedIndices.add(entries.indexOf(resultEntry));
            }
            items.push({ type: "tool", useEntry: entry, resultEntry });
        } else if (entry.type === "progress") {
            items.push({ type: "progress", entry });
        }
        // tool_result entries are consumed by matching tool_use — skip standalone
    }

    return items;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const THINKING_TRUNCATE_THRESHOLD = 500;

function summarizeThinking(content: string): {
    summary: string;
    isTruncated: boolean;
} {
    if (content.length <= THINKING_TRUNCATE_THRESHOLD)
        return { summary: content, isTruncated: false };
    const truncated = content.slice(0, THINKING_TRUNCATE_THRESHOLD);
    const lastPeriod = truncated.lastIndexOf(". ");
    const cutoff =
        lastPeriod > 0 ? lastPeriod + 1 : THINKING_TRUNCATE_THRESHOLD;
    return { summary: content.slice(0, cutoff) + "...", isTruncated: true };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const SpinnerIcon = ({ size = 12 }: { size?: number }) => (
    <Box
        sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: `${spin} 1.1s linear infinite`,
            color: styles.primaryColor,
            flexShrink: 0,
        }}
    >
        <Loader2 size={size} />
    </Box>
);

// ---------------------------------------------------------------------------
// HeroStatusCard
// ---------------------------------------------------------------------------

interface HeroStatusCardProps {
    state: GenerationState;
    error?: string | null;
}

const HeroStatusCard = ({ state, error }: HeroStatusCardProps) => {
    const isError = state === "error";

    return (
        <Paper
            variant="outlined"
            sx={{
                textAlign: "center",
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                borderColor: isError
                    ? "rgba(175,1,1,0.2)"
                    : "rgba(34,56,118,0.15)",
            }}
        >
            <Stack spacing={1.5} alignItems="center">
                {/* Icon container */}
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: isError
                            ? "rgba(175,1,1,0.08)"
                            : "rgba(34,56,118,0.08)",
                    }}
                >
                    {isError ? (
                        <XCircle size={20} color={styles.errorColor} />
                    ) : (
                        <SpinnerIcon size={20} />
                    )}
                </Box>

                {/* Title */}
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {isError
                        ? "Generation failed"
                        : state === "refining"
                          ? "Refining your checklist..."
                          : "Building your checklist..."}
                </Typography>

                {/* Subtitle */}
                <Typography variant="body2" color="text.secondary">
                    {isError
                        ? error || "An unexpected error occurred"
                        : "This typically takes 1–3 minutes"}
                </Typography>
            </Stack>
        </Paper>
    );
};

// ---------------------------------------------------------------------------
// ThinkingEntry
// ---------------------------------------------------------------------------

const ThinkingEntry = ({ entry }: { entry: IEventLogEntry }) => {
    const [expanded, setExpanded] = useState(false);
    const isEmpty = !entry.content;
    const { summary, isTruncated } = isEmpty
        ? { summary: "", isTruncated: false }
        : summarizeThinking(entry.content);

    return (
        <Box
            sx={{
                borderLeft: "3px solid rgba(34,56,118,0.2)",
                background: "rgba(34,56,118,0.03)",
                borderTopRightRadius: "6px",
                borderBottomRightRadius: "6px",
                px: 1.5,
                py: 1,
                animation: `${slideIn} 0.18s ease-out`,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mb: isEmpty ? 0 : 0.5,
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.7rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: styles.primaryColor,
                        fontWeight: 600,
                    }}
                >
                    Thinking
                </Typography>
                {isEmpty && <SpinnerIcon size={10} />}
            </Box>
            {!isEmpty && (
                <Markdown>{expanded ? entry.content : summary}</Markdown>
            )}
            {isTruncated && (
                <Typography
                    component="span"
                    onClick={() => setExpanded((prev) => !prev)}
                    sx={{
                        fontSize: "0.75rem",
                        color: styles.primaryColor,
                        cursor: "pointer",
                        display: "inline-block",
                        mt: 0.5,
                        "&:hover": { textDecoration: "underline" },
                    }}
                >
                    {expanded ? "Show less" : "Show more"}
                </Typography>
            )}
        </Box>
    );
};

// ---------------------------------------------------------------------------
// ToolEntry
// ---------------------------------------------------------------------------

interface ToolEntryProps {
    useEntry: IEventLogEntry;
    resultEntry?: IEventLogEntry;
}

const ToolEntry = ({ useEntry, resultEntry }: ToolEntryProps) => {
    const isActive = !resultEntry;

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 1,
                background: isActive ? "rgba(34,56,118,0.04)" : "#f8f9fc",
                borderRadius: "6px",
                border: isActive
                    ? "1px solid rgba(34,56,118,0.12)"
                    : "1px solid transparent",
                animation: `${slideIn} 0.18s ease-out`,
            }}
        >
            {/* Icon badge */}
            <Box
                sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "4px",
                    bgcolor: "rgba(34,56,118,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                <Wrench size={11} color={styles.primaryColor} />
            </Box>

            {/* Text */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        lineHeight: 1.3,
                    }}
                >
                    {getToolDisplayName(useEntry.toolName || "")}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: "monospace",
                        fontSize: "0.7rem",
                        color: "text.secondary",
                        lineHeight: 1.4,
                    }}
                >
                    {resultEntry ? resultEntry.content : useEntry.content}
                </Typography>
            </Box>

            {/* Status icon */}
            <Box sx={{ flexShrink: 0 }}>
                {isActive ? (
                    <SpinnerIcon size={14} />
                ) : (
                    <CheckCircle2 size={14} color={styles.successColor} />
                )}
            </Box>
        </Box>
    );
};

// ---------------------------------------------------------------------------
// ProgressEntry
// ---------------------------------------------------------------------------

interface ProgressEntryProps {
    entry: IEventLogEntry;
    isLast: boolean;
}

const ProgressEntry = ({ entry, isLast }: ProgressEntryProps) => {
    const isDone = entry.step === "draft_saved" || !isLast;

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 1,
                background: "#f8f9fc",
                borderRadius: "6px",
                border: "1px solid transparent",
                animation: `${slideIn} 0.18s ease-out`,
            }}
        >
            {/* Icon badge */}
            <Box
                sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "4px",
                    bgcolor: "rgba(34,56,118,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                <Database size={11} color={styles.primaryColor} />
            </Box>

            {/* Text */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        lineHeight: 1.3,
                    }}
                >
                    {entry.content}
                </Typography>
            </Box>

            {/* Status icon */}
            <Box sx={{ flexShrink: 0 }}>
                {isDone ? (
                    <CheckCircle2 size={14} color={styles.successColor} />
                ) : (
                    <SpinnerIcon size={14} />
                )}
            </Box>
        </Box>
    );
};

// ---------------------------------------------------------------------------
// AgentTimeline
// ---------------------------------------------------------------------------

interface AgentTimelineProps {
    items: TimelineItem[];
    scrollRef: React.RefObject<HTMLDivElement | null>;
}

const AgentTimeline = ({ items, scrollRef }: AgentTimelineProps) => {
    if (items.length === 0) return null;

    return (
        <Paper variant="outlined">
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.25,
                    borderBottom: "1px solid rgba(0,0,0,0.08)",
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        fontWeight: 600,
                        color: "text.secondary",
                    }}
                >
                    Agent Activity
                </Typography>
            </Box>

            {/* Scrollable body */}
            <Box
                ref={scrollRef}
                sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                {(() => {
                    const lastProgressIdx = items.reduce(
                        (last, item, i) =>
                            item.type === "progress" ? i : last,
                        -1
                    );
                    return items.map((item, idx) => {
                        if (item.type === "thinking") {
                            return (
                                <ThinkingEntry
                                    key={`thinking-${item.entry.iteration}-${item.entry.index}`}
                                    entry={item.entry}
                                />
                            );
                        }
                        if (item.type === "progress") {
                            return (
                                <ProgressEntry
                                    key={`progress-${item.entry.index}`}
                                    entry={item.entry}
                                    isLast={idx === lastProgressIdx}
                                />
                            );
                        }
                        return (
                            <ToolEntry
                                key={`tool-${item.useEntry.iteration}-${item.useEntry.index}`}
                                useEntry={item.useEntry}
                                resultEntry={item.resultEntry}
                            />
                        );
                    });
                })()}
            </Box>
        </Paper>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export interface GenerationEventLogProps {
    entries: IEventLogEntry[];
    state: GenerationState;
    error?: string | null;
    /** Keep the timeline visible even in "complete" state (e.g. during diff review). */
    forceVisible?: boolean;
}

export const GenerationEventLog = ({
    entries,
    state,
    error,
    forceVisible = false,
}: GenerationEventLogProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    // Build timeline items — must be before early returns (hooks rule)
    const timelineItems = useMemo(() => buildTimelineItems(entries), [entries]);

    // Auto-scroll to bottom when new entries arrive during generation/refinement
    useEffect(() => {
        if (
            (state === "generating" || state === "refining") &&
            scrollRef.current
        ) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [entries, state]);

    // Lifecycle: null for idle and complete (unless forceVisible)
    if (state === "idle") return null;
    if (state === "complete" && !forceVisible) return null;
    if (state === "error" && entries.length === 0) return null;

    // When forceVisible in "complete" state, show only the timeline (agent reasoning)
    // without the hero status card (which would confusingly show a spinner)
    const showHero = state !== "complete";

    return (
        <Stack spacing={2}>
            {showHero && <HeroStatusCard state={state} error={error} />}
            <AgentTimeline items={timelineItems} scrollRef={scrollRef} />
        </Stack>
    );
};
